import { useCallback, useEffect } from "react";

/**
 * Custom hook for handling resize functionality in PDF table viewer
 * @param {boolean} isResizing - Current resizing state
 * @param {function} setIsResizing - Function to set resizing state
 * @param {function} setSidebarWidth - Function to set sidebar width
 * @param {React.RefObject} containerRef - Reference to the container element
 * @returns {object} Object containing resize handler functions
 */
export const useResizeHandlers = (
  isResizing,
  setIsResizing,
  setSidebarWidth,
  containerRef
) => {
  // Resize handlers for the sidebar
  const startResize = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);
    },
    [setIsResizing]
  );

  const handleResize = useCallback(
    (e) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      // Calculate percentage from right side (inverted), limit between 20% and 80%
      // When dragging left, PDF gets bigger; when dragging right, PDF gets smaller
      const newWidthPercent = Math.min(
        80,
        Math.max(20, 100 - (mouseX / containerWidth) * 100)
      );
      setSidebarWidth(newWidthPercent);
    },
    [isResizing, setSidebarWidth, containerRef]
  );

  const stopResize = useCallback(() => {
    setIsResizing(false);
  }, [setIsResizing]);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResize);
      document.addEventListener("mouseup", stopResize);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleResize);
        document.removeEventListener("mouseup", stopResize);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleResize, stopResize]);

  return {
    startResize,
    handleResize,
    stopResize,
  };
};
